// ink-component
import React, { useState, useMemo, useEffect, ReactNode } from 'react';

import getSelectedIndexes from './helpers/rows/getSelectedIndexes';
import getOrdering from './helpers/sort/getOrdering';
import getSortQuery from './helpers/sort/getSortQuery';
import getInitialVisibleColumns from './helpers/columns/getInitialVisibleColumns';
import getParsedData from './helpers/data/getParsedData';

import LedgerContext from './contexts/LedgerContext';
import LedgerContent from './LedgerContent';

import getSearchProps from './helpers/props/getSearchProps';
import getPaginationProps from './helpers/props/getPaginationProps';

import getGlobalProps from '../../helpers/constants/global-props';
import useDeprecatedWarning from '../../hooks/useDeprecatedWarning';

import { QueryOptions } from './interfaces';
import { TableProps } from '../Table/Table';
import { TableTwiddleRowProps } from '../Table/TableTwiddleRow/TableTwiddleRow';

interface DefaultCell {
  children?: ReactNode;
  isKey?: boolean;
  shaded?: boolean;
  right?: boolean;
  bold?: boolean;
  actions?: boolean;
  checkbox?: boolean;
  operators?: boolean;
  control?: boolean;
  colSpan?: string;
  highlight?: 'blue' | 'green' | 'red' | 'yellow';
  dividedLeft?: boolean;
}

interface TypeCell {
  type: 'money' | 'number' | 'date';
}

interface CustomCell {
  component?: ReactNode;
  customEmpty?: () => void;
}

interface Header {
  sortable?: boolean;
  right?: boolean;
  help?: ReactNode;
}

interface Column {
  label: string;
  key: string;
  sortBy?: string;
  freeze?: boolean;
  header?: Header;
  cell?: DefaultCell | CustomCell | TypeCell;
}

interface Pagination {
  component?: ReactNode;
  currentPage: number;
  totalPages: number;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: Function;
}

interface Search {
  id?: string;
  onChange: (e: Event) => void;
  placeholder?: string;
  onChangeDelay?: number;
  value?: string;
}

interface Shimmer {
  loading: boolean;
  quantity: number;
}

interface Item {
  label: string;
  value: string;
  checked: boolean;
}

interface Group {
  label: string;
  key: string;
  items: Array<Item>;
}

interface Filter {
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: Function;
  groups: Array<Group>;
}

interface EmptyState {
  text: ReactNode;
  children: ReactNode;
  type?: ReactNode;
  icon?: string;
}

interface Definition {
  text: string;
  color: string;
  value: any;
}

interface Ribbon {
  key: string;
  definitions: Array<Definition>;
}

interface BaseLedgerProps {
  /** Unique identifier */
  id: string;
  /** Defines the cell and header properties, configurations and bindings columns.
   * The order of the objects in the array is the representation of the indexes.
   * The columns can be frozen to the left by passing the `freeze` prop to them.
   * Multiple frozen columns must be done in a sequence, starting from the first column
   * to the last frozen one, never skipping any in between.
   * Also, columns have a `header` property that can make the header sortable and/or right aligned.
   * Last but not least, columns have a `cell` property that have three different behaviors:
   * **1.** A simple object with `bold` and/or `right` fields
   * **2.** Or, some prefined cells for `money`, `number` or `date`
   * **3.** Or, any custom component that you create.
   * The `cell` property object is spread into the TableCell internal Component.
   * So you can pass anything that a [TableCell](/components/TableCell/) receives to it.
   * */
  columns: Array<Column>;
  /** Some actions that each row can have. Try to use Ink.Dropdown */
  // eslint-disable-next-line @typescript-eslint/ban-types
  rowActions?: Function;
  /** Actions for when some rows on the Table are checked */
  // eslint-disable-next-line @typescript-eslint/ban-types
  bulkActions?: Function;
  /** Customized filter component */
  // eslint-disable-next-line @typescript-eslint/ban-types
  filterComponent?: Function;
  /** Add a twiddle behavior for the Ledger */
  // eslint-disable-next-line @typescript-eslint/ban-types
  twiddle?: Function;
  /** Add default props for twiddle rows */
  twiddleRowAttrs?: ({ row: any, index: number }) => TableTwiddleRowProps;
  /** The Table's pagination, pass null if you don't want it */
  pagination?: Pagination;
  /** Ledger's actions, try to use Ink.Button and/or Ink.Dropdown */
  // eslint-disable-next-line @typescript-eslint/ban-types
  actions?: Function;
  /** Hide/show columns of the Table */
  visibleColumns?: Array<string>;
  /** Search input, pass null if you don't want it */
  search?: Search | null;
  /** The Table's filter. The onChange is only needed when advanced custom filter is used with RestLedger. */
  filter?: Filter;
  /** Empty State */
  emptyState?: EmptyState;
  /** Callback for the Table's row click */
  // eslint-disable-next-line @typescript-eslint/ban-types
  onClickRow?: Function;
  /** Table's ribbons */
  ribbon?: Ribbon;
  /** Control the selected rows. This is only required when you need to select/deselect rows by yourself */
  selectedRows?: Array<number>;
  /** Fires when a Bulk Actions row is selected/unselected. Returns the index of the currently selected rows. */
  // eslint-disable-next-line @typescript-eslint/ban-types
  onSelectRow?: Function;
  /** Modifier for mini Tables */
  mini?: boolean;
  /** Modifier for hover Tables */
  hover?: boolean;
  /** Enforces column widths in pixels to be calculated prior to percentage ones. Try it if you are passing pixel values on the `width` prop that are not being rendered as expected. */
  fixed?: boolean;
  /** Modifier for vertical padding */
  density?: 'low' | 'high';
  /** Defines if the table will be horizontal scrollable or not. */
  hasScroll?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  data: Array<any>;
  /** @ignore */
  shimmer?: Shimmer;
  /** @ignore */
  sort?: (query: string) => void;
  /** @ignore */
  ordering?: string;
  /** @ignore */
  onChangePage?: () => void;
  /** @ignore */
  onChangeSearch?: () => void;
  /** @ignore */
  totalPages?: number;
  /** @ignore */
  onChangeFilter?: () => void;
  /** @ignore */
  queryOptions?: QueryOptions;
}

export type LedgerProps = Omit<TableProps, 'sort' | 'headers' | 'onSelect' | 'onSelectAll' | 'renderRow'> &
  BaseLedgerProps;

const globalProps = getGlobalProps({ deprecated: true });

/**
 * @purpose Aggregates a table, filters, and actions
 * @categories table
 * @deprecated This component is deprecated and replaced by [useLedger](https://ink.carta.com/hooks/use-ledger)
 * @status deprecated
 * @alternatePatternLink /hooks/use-ledger
 * @dateOfDeletion December 1st, 2023
 */
function Ledger(props: LedgerProps) {
  const { id, columns, data, shimmer, ordering, search, pagination, selectedRows, onSelectRow } = props;
  useDeprecatedWarning({ componentName: 'Ledger', substitute: 'useLedger' });

  const [selected, setSelected] = useState(selectedRows || []);
  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns(props.visibleColumns, columns));

  useEffect(() => {
    setSelected(selectedRows || []);
  }, [selectedRows]);

  const onSelect = newSelected => {
    const selected = getSelectedIndexes(data)(newSelected);
    setSelected(selected);
    if (onSelectRow) {
      onSelectRow({ selected });
    }
  };

  const onSort = sortedColumn => {
    const { sort, columns } = props;
    const sortQuery = getSortQuery(columns, sortedColumn);
    if (sort) {
      sort(sortQuery);
    }
  };

  const bulkActionsProps = bulkActions =>
    bulkActions
      ? {
          selectable: 'all',
          onSelect,
          onSelectAll: onSelect,
        }
      : null;

  const ledgerState = useMemo(
    () => ({
      selected,
      visibleColumns,
      data: getParsedData(shimmer, columns, data),
      updateVisibleColumns: setVisibleColumns,
      clearSelect: () => {
        setSelected([]);
        if (onSelectRow) {
          onSelectRow({ selected: [] });
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, visibleColumns, shimmer, data],
  );

  return (
    <LedgerContext.Provider value={ledgerState}>
      <LedgerContent
        {...props}
        bulkActionsProps={bulkActionsProps}
        sort={onSort}
        ordering={getOrdering(ordering)}
        search={getSearchProps(search, id)}
        pagination={getPaginationProps(pagination)}
        data-testid={props['data-testid']}
        {...globalProps}
      />
    </LedgerContext.Provider>
  );
}

Ledger.defaultProps = {
  data: null,
  rowActions: null,
  bulkActions: null,
  filterComponent: null,
  twiddle: null,
  twiddleRowAttrs: null,
  onClickRow: null,
  pagination: {},
  search: {},
  filter: null,
  actions: null,
  visibleColumns: null,
  emptyState: {
    text: 'No data added',
    type: 'page',
    children: null,
  },
  ribbon: null,
  selectedRows: null,
  shimmer: {
    loading: false,
    quantity: 50,
  },
  sort: () => {},
  onSelectRow: () => {},
  ordering: '',
  mini: false,
  hover: false,
  fixed: false,
  density: null,
  hasScroll: false,
  'data-testid': undefined,
};

Ledger.displayName = 'Ledger';

export default Ledger;
